import { supabase } from '@/lib/supabase';
import { Store } from '@/types/models/Store';
import { AppConfig, activeAppConfig } from '@/constants/appConfig';
import { logger } from '@/lib/logger';
import { handleSupabaseError } from '@/lib/supabase';
import { LLMService } from '@/services/ai';
import { STORE_ROLES, VALID_STORE_ROLES } from '@/constants/roles';

async function getLocaleFields(name: string, address: string | null | undefined, language_locale: string): Promise<{ name_locale: string; address_locale: string | null }> {
  try {
    const [name_locale, address_locale] = await Promise.all([
      LLMService.translate(name, language_locale).catch((err) => {
        logger.warn('Failed to translate store name', { error: err });
        return name;
      }),
      address ? LLMService.translate(address, language_locale).catch((err) => {
        logger.warn('Failed to translate store address', { error: err });
        return address;
      }) : Promise.resolve(null)
    ]);
    return { name_locale, address_locale };
  } catch (err) {
    logger.warn('Failed to translate store name/address', { error: err });
    return { name_locale: name, address_locale: address || null };
  }
}

export class StoreService {
  static async getAll(): Promise<Store[]> {
    try {
      const { data, error } = await supabase
        .from('stores')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw new Error(`Failed to fetch stores: ${error.message}`);
      if (!data) throw new Error('No stores data received');

      return data;
    } catch (error) {
      throw new Error(`Store service error: ${error instanceof Error ? error.message : 'Unknown error occurred'}`);
    }
  }

  static async getById(id: string, currentUserId: string): Promise<Store> {
    try {
      if (!id) throw new Error('Store ID is required');
      if (!currentUserId) throw new Error('Current user ID is required');
      const { data, error } = await supabase
        .from('stores')
        .select('*, store_admins(user_id, role, created_at, updated_at)')
        .eq('id', id)
        .single();
      if (error) throw new Error(`Failed to fetch store: ${error.message}`);
      if (!data) throw new Error(`Store not found with ID: ${id}`);
      const admins = data.store_admins || [];
      const userRole = admins.find((a: any) => a.user_id === currentUserId)?.role;
      return { ...data, admins, userRole };
    } catch (error) {
      throw new Error(`Store service error: ${error instanceof Error ? error.message : 'Unknown error occurred'}`);
    }
  }

  static async create(data: { name: string; address?: string | null; phone?: string | null; language_locale?: string; store_config?: { store_type?: string } }): Promise<Store> {
    try {
      const language_locale = data.language_locale || 'English';
      const { name_locale, address_locale } = await getLocaleFields(data.name, data.address, language_locale);
      const insertData: any = { ...data, name_locale, address_locale, language_locale };


      const { data: store, error } = await supabase
        .from('stores')
        .insert([insertData])
        .select()
        .single();
      if (error) throw new Error(`Failed to create store: ${error.message}`);
      if (!store) throw new Error('Store was not created');
      return store;
    } catch (error) {
      throw new Error(`Store service error: ${error instanceof Error ? error.message : 'Unknown error occurred'}`);
    }
  }

  static async update(id: string, data: { name?: string; address?: string | null; phone?: string | null; language_locale?: string; online_store_enabled?: boolean; [key: string]: any }): Promise<Store> {
    try {
      if (!id) throw new Error('Store ID is required');
      let name_locale: string | undefined = undefined;
      let address_locale: string | null | undefined = undefined;
      if (data.name) {
        const language_locale = data.language_locale || 'English';
        const localeFields = await getLocaleFields(data.name, data.address, language_locale);
        name_locale = localeFields.name_locale;
        address_locale = localeFields.address_locale;
      }
      const updateData: any = { ...data };
      if (name_locale !== undefined) updateData['name_locale'] = name_locale;
      if (address_locale !== undefined) updateData['address_locale'] = address_locale;
      if (data.language_locale) updateData['language_locale'] = data.language_locale;
      
      // First, perform the update
      const { data: updateResult, error: updateError } = await supabase
        .from('stores')
        .update(updateData)
        .eq('id', id)
        .select('id');

      if (updateError) throw new Error(`Failed to update store: ${updateError.message}`);
      
      // Verify that the update actually affected a row
      if (!updateResult || updateResult.length === 0) {
        throw new Error(`Store update did not affect any rows. Store may not exist or you may not have permission to update it.`);
      }

      // Then, fetch the updated store separately to avoid .single() issues
      const { data: store, error: selectError } = await supabase
        .from('stores')
        .select('*, store_admins(user_id, role, created_at, updated_at)')
        .eq('id', id)
        .maybeSingle();

      if (selectError) throw new Error(`Failed to fetch updated store: ${selectError.message}`);
      if (!store) throw new Error(`Store not found with ID: ${id}`);

      const admins = store.store_admins || [];
      return { ...store, admins };
    } catch (error) {
      throw new Error(`Store service error: ${error instanceof Error ? error.message : 'Unknown error occurred'}`);
    }
  }

  static async delete(id: string): Promise<void> {
    try {
      if (!id) throw new Error('Store ID is required');

      // Use the delete_store_data SQL function instead of basic delete
      const { error } = await supabase
        .rpc('delete_store_data', { store_id_to_delete: id });

      if (error) throw new Error(`Failed to delete store: ${error.message}`);
    } catch (error) {
      throw new Error(`Store service error: ${error instanceof Error ? error.message : 'Unknown error occurred'}`);
    }
  }

  static async updateStoreConfig(id: string, storeConfig: any): Promise<Store> {
    try {
      if (!id) throw new Error('Store ID is required');

      const { data: store, error } = await supabase
        .from('stores')
        .update({ store_config: storeConfig })
        .eq('id', id)
        .select('*, store_admins(user_id, role, created_at, updated_at)')
        .single();

      if (error) throw new Error(`Failed to update store config: ${error.message}`);
      if (!store) throw new Error(`Store not found with ID: ${id}`);

      const admins = store.store_admins || [];
      return { ...store, admins };
    } catch (error) {
      throw new Error(`Store service error: ${error instanceof Error ? error.message : 'Unknown error occurred'}`);
    }
  }

  static async updateUnits(id: string, units: { name: string; code: string; divisible: boolean; }[]): Promise<Store> {
    try {
      if (!id) throw new Error('Store ID is required');

      const { data: store, error } = await supabase
        .from('stores')
        .update({ units })
        .eq('id', id)
        .select('*, store_admins(user_id, role, created_at, updated_at)')
        .single();

      if (error) throw new Error(`Failed to update store units: ${error.message}`);
      if (!store) throw new Error(`Store not found with ID: ${id}`);

      const admins = store.store_admins || [];
      return { ...store, admins };
    } catch (error) {
      throw new Error(`Store service error: ${error instanceof Error ? error.message : 'Unknown error occurred'}`);
    }
  }

  static async getStoreAppConfig(storeId: string): Promise<AppConfig> {
    try {
      logger.info('Fetching store app config', { storeId });

      const { data, error } = await supabase
        .from('stores')
        .select('app_config')
        .eq('id', storeId)
        .single();

      if (error) {
        logger.error('Failed to fetch store app config', { error, storeId });
        throw error;
      }

      // Get store config, defaulting to empty object if null
      const storeConfig: Partial<AppConfig> = data?.app_config || {};

      // Deep merge store config with active config
      const mergedConfig: AppConfig = {
        ...activeAppConfig,
        munshiJi: {
          ...activeAppConfig.munshiJi,
          ...(storeConfig.munshiJi || {}),
        },
        invoice: {
          ...activeAppConfig.invoice,
          ...(storeConfig.invoice || {}),
        },
        ledger: {
          ...activeAppConfig.ledger,
          ...(storeConfig.ledger || {}),
        },
        platform: {
          ...activeAppConfig.platform,
          ...(storeConfig.platform || {}),
        },
        analytics: {
          ...activeAppConfig.analytics,
          ...(storeConfig.analytics || {}),
        },
        onboarding: {
          ...activeAppConfig.onboarding,
          ...(storeConfig.onboarding || {}),
        },
      };

      logger.debug('Store app config fetched and merged successfully', {
        storeId,
        storeConfig,
        mergedConfig
      });

      return mergedConfig;
    } catch (error) {
      logger.error('Error in getStoreAppConfig', { error, storeId });
      throw handleSupabaseError(error);
    }
  }

  static async updateAppConfig(storeId: string, partialConfig: Partial<AppConfig>): Promise<Store> {
    try {
      if (!storeId) throw new Error('Store ID is required');

      logger.info('Updating store app_config', { storeId, partialConfig });

      // Fetch current app_config
      const { data: currentStore, error: fetchError } = await supabase
        .from('stores')
        .select('app_config')
        .eq('id', storeId)
        .single();

      if (fetchError) {
        logger.error('Failed to fetch current app_config', { error: fetchError, storeId });
        throw new Error(`Failed to fetch store app_config: ${fetchError.message}`);
      }

      // Merge current config with new partial config
      const currentConfig: Partial<AppConfig> = currentStore?.app_config || {};
      const mergedConfig = {
        ...currentConfig,
        ...partialConfig,
        // Deep merge nested objects
        ...(partialConfig.onboarding && {
          onboarding: {
            ...(currentConfig.onboarding || {}),
            ...partialConfig.onboarding,
          },
        }),
      };

      // Update store with merged app_config
      const { data: store, error } = await supabase
        .from('stores')
        .update({ app_config: mergedConfig })
        .eq('id', storeId)
        .select('*, store_admins(user_id, role, created_at, updated_at)')
        .single();

      if (error) {
        logger.error('Failed to update app_config', { error, storeId });
        throw new Error(`Failed to update app_config: ${error.message}`);
      }

      if (!store) {
        throw new Error(`Store not found with ID: ${storeId}`);
      }

      logger.info('Successfully updated store app_config', { storeId });

      const admins = store.store_admins || [];
      return { ...store, admins };
    } catch (error) {
      logger.error('Error in updateAppConfig', { error, storeId });
      throw handleSupabaseError(error);
    }
  }

  static async getStoreCount(userId: string): Promise<number> {
    try {
      if (!userId) throw new Error('User ID is required');

      logger.info('Fetching store count for user', { userId });

      const { data, error } = await supabase
        .from('stores')
        .select('id', { count: 'exact' })
        .eq('owner_id', userId);

      if (error) {
        logger.error('Failed to fetch store count', { error, userId });
        throw error;
      }

      return data?.length || 0;
    } catch (error) {
      logger.error('Error in getStoreCount', { error });
      throw handleSupabaseError(error);
    }
  }

  static async getAllByAdmin(userId: string): Promise<Array<{ store: Store; role: string }>> {
    try {
      if (!userId) throw new Error('User ID is required');
      const { data, error } = await supabase
        .from('store_admins')
        .select('role, stores(*)')
        .eq('user_id', userId);
      if (error) throw new Error(error.message);
      // Return array of { store, role }
      return (data || [])
        .filter((row: any) => row.stores)
        .map((row: any) => ({ store: row.stores, role: row.role }));
    } catch (error) {
      throw new Error(`Store service error: ${error instanceof Error ? error.message : 'Unknown error occurred'}`);
    }
  }

  static async getStoreAdmins(storeId: string): Promise<Array<{ id: string; role: string; phone?: string; email?: string }>> {
    try {
      if (!storeId) throw new Error('Store ID is required');
      // Fetch store owner_id and all admins in a single call
      const { data, error } = await supabase
        .from('stores')
        .select('owner_id, store_admins(user_id, role)')
        .eq('id', storeId)
        .single();
      if (error) throw new Error(error.message);
      const admins: Array<{ id: string; role: string }> = [];
      // Add owner
      if (data.owner_id) {
        admins.push({ id: data.owner_id, role: STORE_ROLES.OWNER });
      }
      // Add admins (avoid duplicate owner)
      if (Array.isArray(data.store_admins)) {
        for (const admin of data.store_admins) {
          if (!admin.user_id) continue;
          if (admins.some(a => a.id === admin.user_id)) continue;
          admins.push({ id: admin.user_id, role: admin.role || STORE_ROLES.MANAGER });
        }
      }
      // Fetch user details for all unique admin IDs
      const userIds = admins.map(a => a.id);
      if (userIds.length === 0) return [];
      const { data: users, error: userError } = await supabase
        .from('users')
        .select('uid, phone, email')
        .in('uid', userIds);
      if (userError) throw new Error(userError.message);
      // Map user details to admins
      const userMap = new Map<string, { phone?: string; email?: string }>();
      for (const user of users || []) {
        userMap.set(user.uid, { phone: user.phone || undefined, email: user.email || undefined });
      }
      return admins.map(a => {
        const userDetails = userMap.get(a.id);
        return {
          id: a.id,
          role: a.role,
          phone: userDetails?.phone,
          email: userDetails?.email,
        };
      }) as Array<{ id: string; role: string; phone?: string; email?: string }>;
    } catch (error) {
      throw new Error(`Failed to fetch store admins: ${error instanceof Error ? error.message : 'Unknown error occurred'}`);
    }
  }

  static async addStoreAdmin(storeId: string, phone: string, role: string): Promise<void> {
    try {
      if (!storeId) throw new Error('Store ID is required');
      if (!phone) throw new Error('Phone number is required');
      if (!role) throw new Error('Role is required');

      // Validate phone number format (10 digits)
      const phoneRegex = /^\d{10}$/;
      if (!phoneRegex.test(phone)) {
        throw new Error('Phone number must be exactly 10 digits');
      }

      // Validate role
      if (!VALID_STORE_ROLES.includes(role as any)) {
        throw new Error(`Invalid role. Must be one of: ${VALID_STORE_ROLES.join(', ')}`);
      }

      // First, check if user exists with this phone number
      const { data: existingUsers, error: userError } = await supabase
        .from('users')
        .select('uid, email, created_at')
        .eq('phone', phone);

      if (userError) {
        throw new Error(`Failed to check user: ${userError.message}`);
      }

      if (!existingUsers || existingUsers.length === 0) {
        throw new Error('No user found with this phone number. User must be registered first.');
      }

      let selectedUser;
      const sortedUsers = existingUsers.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      const usersWithNullEmail = sortedUsers.filter(u => u.email === null);

      if (usersWithNullEmail.length > 0) {
        selectedUser = usersWithNullEmail[0];
      } else {
        selectedUser = sortedUsers[0];
      }

      const existingUser = selectedUser!;

      // Check if user is already an admin for this store
      const { data: existingAdmin, error: adminCheckError } = await supabase
        .from('store_admins')
        .select('user_id')
        .eq('store_id', storeId)
        .eq('user_id', existingUser.uid)
        .single();

      if (adminCheckError && adminCheckError.code !== 'PGRST116') {
        throw new Error(`Failed to check existing admin: ${adminCheckError.message}`);
      }

      if (existingAdmin) {
        throw new Error('User is already an admin for this store');
      }

      // Add user as admin to the store
      const { error: insertError } = await supabase
        .from('store_admins')
        .insert([{
          store_id: storeId,
          user_id: existingUser.uid,
          role: role
        }]);

      if (insertError) {
        throw new Error(`Failed to add store admin: ${insertError.message}`);
      }

      logger.info('Store admin added successfully', { storeId, userId: existingUser.uid, role });
    } catch (error) {
      throw new Error(`Failed to add store admin: ${error instanceof Error ? error.message : 'Unknown error occurred'}`);
    }
  }

  static async removeStoreAdmin(storeId: string, userId: string): Promise<void> {
    if (!storeId) throw new Error('Store ID is required');
    if (!userId) throw new Error('User ID is required');
    // Prevent removing owner via this method (should only remove from store_admins)
    // (UI should prevent, but double check here)
    // Check if user is owner
    const { data: store, error: storeError } = await supabase
      .from('stores')
      .select('owner_id')
      .eq('id', storeId)
      .single();
    if (storeError) throw new Error('Failed to check store owner');
    if (store && store.owner_id === userId) {
      throw new Error('Cannot remove the store owner');
    }
    // Remove from store_admins
    const { error } = await supabase
      .from('store_admins')
      .delete()
      .eq('store_id', storeId)
      .eq('user_id', userId);
    if (error) throw new Error(`Failed to remove store admin: ${error.message}`);
  }

  static async getAllWithOwners(): Promise<Array<{
    id: string;
    name: string;
    address: string;
    phone: string;
    created_at: string;
    owner_name: string | null;
    owner_phone: string | null;
    owner_email: string | null;
  }>> {
    try {
      const { data, error } = await supabase.rpc('get_stores_with_owners');
      if (error) throw new Error(`Failed to fetch stores with owners: ${error.message}`);
      if (!data) throw new Error('No stores data received');
      return data;
    } catch (error) {
      throw new Error(`Store service error: ${error instanceof Error ? error.message : 'Unknown error occurred'}`);
    }
  }

  /**
   * Add new categories to a store's categories list
   * Categories are stored as text array in the database
   */
  static async addCategories(storeId: string, newCategories: string[]): Promise<void> {
    try {
      if (!storeId || !newCategories || newCategories.length === 0) {
        return;
      }

      // Fetch current store to get existing categories
      const { data: store, error: fetchError } = await supabase
        .from('stores')
        .select('categories')
        .eq('id', storeId)
        .single();

      if (fetchError) {
        logger.error('Failed to fetch store categories', { error: fetchError, storeId });
        throw new Error(`Failed to fetch store: ${fetchError.message}`);
      }

      // Parse existing categories (stored as text array)
      let existingCategories: string[] = [];
      if (store?.categories && Array.isArray(store.categories)) {
        // Categories are stored as text array
        existingCategories = store.categories
          .filter((cat: any) => cat && typeof cat === 'string');
      }

      // Merge new categories with existing ones (avoid duplicates)
      const allCategories = [...new Set([...existingCategories, ...newCategories])];

      const { error: updateError } = await supabase
        .from('stores')
        .update({ categories: allCategories })
        .eq('id', storeId);

      if (updateError) {
        logger.error('Failed to update store categories', { error: updateError, storeId, newCategories });
        throw new Error(`Failed to update store categories: ${updateError.message}`);
      }

      logger.info('Store categories updated successfully', {
        storeId,
        addedCategories: newCategories,
        totalCategories: allCategories.length
      });
    } catch (error) {
      logger.error('Error adding categories to store', { error, storeId, newCategories });
      throw error;
    }
  }
}